import { api } from "./api";
import { AxiosError } from "axios";
import type {
  PaginatedUsersResponse,
  UserType,
  UserResponse,
  UpdateUserData,
} from "@/types/user.type";

// Récupère tous les utilisateurs d'une entreprise (@)
export const getCompanyUsers = async (
  companyId: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
  }
) => {
  try {
    // Construction des paramètres de la requête
    const queryParams = new URLSearchParams();
    // Ajout de l'ID de l'entreprise
    queryParams.append("companyId", companyId);
    // Ajout des paramètres de pagination
    if (params?.page) queryParams.append("page", params.page.toString()); // si page est défini, ajoutez-la à la requête
    if (params?.limit) queryParams.append("limit", params.limit.toString()); // si limit est défini, ajoutez-la à la requête
    if (params?.search) queryParams.append("search", params.search); // si search est défini, ajoutez-la à la requête

    // Requête pour récupérer les utilisateurs de l'entreprise avec la pagination
    const response = await api.get<PaginatedUsersResponse>(
      `/users/by-company?${queryParams}`
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la récupération des utilisateurs"
      );
    }
    throw new Error("Erreur lors de la récupération des utilisateurs");
  }
};

// Récupère tous les utilisateurs (@)
export const getUsers = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  role?: "SUPERUSER" | "ADMIN" | "EMPLOYEE";
  companyId?: string;
}) => {
  try {
    const response = await api.get<PaginatedUsersResponse>("/users", {
      params,
    });
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la récupération des utilisateurs"
      );
    }
    throw new Error("Erreur lors de la récupération des utilisateurs");
  }
};

// Récupère un utilisateur par son ID (@)
export const getUserById = async (id: string) => {
  try {
    const response = await api.get<UserResponse>(`/users/${id}`);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la récupération de l'utilisateur"
      );
    }
    throw new Error("Erreur lors de la récupération de l'utilisateur");
  }
};

//Créer un administrateur (@)
export const createAdmin = async (user: UserType) => {
  try {
    const response = await api.post<UserResponse>("/users", user);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la création de l'utilisateur"
      );
    }
    throw new Error("Erreur lors de la création de l'utilisateur");
  }
};

//Créer un employé (@)
export const createEmployee = async (user: UserType) => {
  try {
    const response = await api.post<UserResponse>("/users/employee", user);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la création de l'utilisateur"
      );
    }
    throw new Error("Erreur lors de la création de l'utilisateur");
  }
};

// Modifier un utilisateur (@)
export const updateUser = async (id: string, data: UpdateUserData) => {
  try {
    const response = await api.patch<UserResponse>(`/users/${id}`, data);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la modification de l'utilisateur"
      );
    }
    throw new Error("Erreur lors de la modification de l'utilisateur");
  }
};

// Supprimer un utilisateur (@)
export const deleteUser = async (userId: string) => {
  try {
    const response = await api.delete<UserResponse>(`/users/${userId}`);
    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      throw new Error(
        error.response?.data?.message ||
          "Erreur lors de la suppression de l'utilisateur"
      );
    }
    throw new Error("Erreur lors de la suppression de l'utilisateur");
  }
};
